import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  ArrowDownIcon,
  ArrowUpIcon,
  CircleCheckIcon,
  PencilIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "@lucide/icons";

import { Button } from "simplydo/react/ui/button";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";
import { Segment } from "simplydo/react/ui/segment";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { UserChooser } from "components/lib/Choosers";

import type { PointOfContactRole } from "../../types";

const PointOfContact = () => {
  const [updatingRole, setUpdatingRole] = useState("");
  const user = useAppSelector((state) => state.user);
  const ownerOrganisation = user?.ownerOrganisation;
  const orgId = ownerOrganisation?._id;
  const { t } = useTranslation();

  const [pointOfContactRoles, setPointOfContactRoles] = useState<
    PointOfContactRole[]
  >([]);

  useEffect(() => {
    if (!orgId) return;
    api.organisations.getPointOfContactRoles(
      orgId,
      ({ pointOfContactRoles: newRoles }) => {
        setPointOfContactRoles(newRoles);
      },
      () => {},
    );
  }, [orgId]);

  if (!orgId || !ownerOrganisation) {
    return null;
  }

  return (
    <Segment>
      <Modal open={!!updatingRole}>
        <ModalHeader>Update point of contact role</ModalHeader>
        <ModalContent>
          <H3>
            Name
            <Subheader>
              Give this point of contact a descriptive title. This will appear
              to any user seeking help and will make it easier for them to
              decide who to contact. Make sure this describes the nature of
              support they should be seeking when contacting this user. E.g.,
              &quot;
              {t("common:capitalise", { key: "generic.idea" })}{" "}
              submissions&quot; or &quot;General queries&quot;.
            </Subheader>
          </H3>
          <Input
            fluid
            value={pointOfContactRoles.find((r) => r.id === updatingRole)?.name}
            onChange={(_e, { value }) => {
              setPointOfContactRoles(
                pointOfContactRoles.map((r) => {
                  if (r.id === updatingRole) {
                    return { ...r, name: value };
                  }
                  return r;
                }),
              );
            }}
          />
          <H3>
            Description
            <Subheader>
              This description will also be shown to the user and gives them
              further direction of how to ask this point of contact for help.
            </Subheader>
          </H3>
          <Input
            fluid
            value={
              pointOfContactRoles.find((r) => r.id === updatingRole)
                ?.description
            }
            onChange={(_e, { value }) => {
              setPointOfContactRoles(
                pointOfContactRoles.map((r) => {
                  if (r.id === updatingRole) {
                    return { ...r, description: value as string };
                  }
                  return r;
                }),
              );
            }}
          />
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() => {
              setUpdatingRole("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              const role = pointOfContactRoles.find(
                (r) => r.id === updatingRole,
              );
              if (!role) {
                return;
              }
              api.organisations.updatePointOfContactRole(
                orgId,
                role.id,
                { name: role.name, description: role.description ?? "" },
                ({ pointOfContactRoles: newRoles }) => {
                  setPointOfContactRoles(newRoles);
                  setUpdatingRole("");
                },
                () => {},
              );
            }}
            color="primary"
          >
            Save
          </Button>
        </ModalActions>
      </Modal>

      <H3>
        Point of contact users
        <Subheader>
          Choose users in your organisation to be a point of contact for support
          for other users in {ownerOrganisation.name}. For example, someone who
          can be contacted if a user needs help using the platform. Please note
          that the selected users email addresses will be available to other
          users in {ownerOrganisation.name}.
        </Subheader>
      </H3>

      <Table style={{ maxWidth: "100%" }}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell collapsing></TableHeaderCell>
            <TableHeaderCell collapsing></TableHeaderCell>
            <TableHeaderCell>Role</TableHeaderCell>
            <TableHeaderCell>Assigned to</TableHeaderCell>
            <TableHeaderCell></TableHeaderCell>
            <TableHeaderCell>Description</TableHeaderCell>
            <TableHeaderCell textAlign="right"></TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pointOfContactRoles.map((role, index) => (
            <TableRow key={role.id}>
              <TableCell textAlign="right">
                {!role.assignedTo ? (
                  <Popup
                    trigger={
                      <Icon
                        icon={TriangleAlertIcon}
                        color="yellow"
                        size="large"
                      />
                    }
                    content="This role is currently not visible to your users because it has not been assigned to a user."
                    position="top center"
                  />
                ) : (
                  <Icon icon={CircleCheckIcon} color="green" size="large" />
                )}
              </TableCell>
              <TableCell>
                <div style={{ display: "flex" }}>
                  <Button
                    disabled={index === 0}
                    size="small"
                    onClick={() => {
                      api.organisations.reorderPointOfContactRole(
                        ownerOrganisation._id,
                        role.id,
                        "up",
                        ({ pointOfContactRoles: newRoles }) => {
                          setPointOfContactRoles(newRoles);
                        },
                        () => {},
                      );
                    }}
                  >
                    <Icon icon={ArrowUpIcon} />
                  </Button>
                  <Button
                    disabled={index === pointOfContactRoles.length - 1}
                    size="small"
                    onClick={() => {
                      api.organisations.reorderPointOfContactRole(
                        ownerOrganisation._id,
                        role.id,
                        "down",
                        ({ pointOfContactRoles: newRoles }) => {
                          setPointOfContactRoles(newRoles);
                        },
                        () => {},
                      );
                    }}
                  >
                    <Icon icon={ArrowDownIcon} />
                  </Button>
                </div>
              </TableCell>
              <TableCell collapsing>{role.name}</TableCell>
              {role.assignedTo ? (
                <>
                  <TableCell collapsing>
                    <UserChip user={role.ownerAssignedTo} />
                  </TableCell>
                  <TableCell collapsing>
                    <Button
                      size="small"
                      onClick={() => {
                        util
                          .confirm(
                            "Remove point of contact",
                            `Are you sure you want to remove "${role?.ownerAssignedTo?.profile?.fullName || "Deleted user"}" as the assigned contact for your users if the need help regarding "${role.name}"?`,
                          )
                          .then(() => {
                            api.organisations.unassignPointOfContact(
                              ownerOrganisation._id,
                              role.id,
                              role.assignedTo,
                              ({ pointOfContactRoles: newRoles }) => {
                                setPointOfContactRoles(newRoles);
                              },
                              () => {},
                            );
                          })
                          .catch(() => {});
                      }}
                    >
                      Unassign
                    </Button>
                  </TableCell>
                </>
              ) : (
                <TableCell colSpan={2} textAlign="right">
                  <UserChooser
                    single
                    trigger={<Button size="small">Assign user</Button>}
                    enabledFeatures={{ search: true }}
                    searchFunction={(query, success, failure) => {
                      if (!query) {
                        success([]);
                        return;
                      }
                      api.search.omni(
                        { query, include: ["users"] },
                        ({ users }) => users && success(users),
                        failure,
                      );
                    }}
                    onComplete={(users) => {
                      const selectedUser = users[0];
                      if (!selectedUser?._id) {
                        return;
                      }

                      api.organisations.assignPointOfContact(
                        orgId,
                        role.id,
                        selectedUser._id,
                        ({ pointOfContactRoles: newRoles }) => {
                          setPointOfContactRoles(newRoles);
                        },
                        () => {},
                      );
                    }}
                  />
                </TableCell>
              )}
              <TableCell
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 150,
                }}
              >
                {role.description}
              </TableCell>
              <TableCell textAlign="right" collapsing>
                <div style={{ display: "flex" }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setUpdatingRole(role.id);
                    }}
                  >
                    <Icon icon={PencilIcon} />
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      util
                        .confirm(
                          "Delete point of contact role",
                          `Are you sure you want to delete the point of contact role "${role.name}"?`,
                        )
                        .then(() => {
                          api.organisations.deletePointOfContactRole(
                            ownerOrganisation._id,
                            role.id,
                            ({ pointOfContactRoles: newRoles }) => {
                              setPointOfContactRoles(newRoles);
                            },
                            () => {},
                          );
                        })
                        .catch(() => {});
                    }}
                    variant="outline"
                  >
                    <Icon icon={Trash2Icon} />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={7} textAlign="right">
              <Button
                onClick={() => {
                  util
                    .prompt(
                      "Create point of contact role",
                      `Please give this point of contact a title. Make sure this describes the nature of support they should be seeking when contacting this user. E.g., "${t(
                        "common:capitalise",
                        { key: "generic.idea" },
                      )} submissions" or "General queries".`,
                      (roleName) => {
                        api.organisations.createPointOfContactRole(
                          orgId,
                          { name: roleName },
                          ({ pointOfContactRoles: newRoles }) => {
                            setPointOfContactRoles(newRoles);
                          },
                          () => {},
                        );
                      },
                    )
                    .catch(() => {});
                }}
              >
                Create new role
              </Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Segment>
  );
};

export default PointOfContact;
